import {
  Container,
  HeaderIconWrapper,
  LeftSide,
  NavigationLabel,
  RightSide,
  RowSpaceBetween,
  SelectedModeLabel,
  SwitchersWrapper,
} from './Header.styled';
import { ReactComponent as LogyxLogoSVG } from 'assets/icons/logyxLogo.svg';
import { useLocation } from 'react-router-dom';
import { RoutesConfig } from 'navigation/routes';
import SubHeader from 'components/SubHeader/SubHeader';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { useRef, useState } from 'react';
import LanguageSwitcher from 'components/LanguageSwitcher/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useOutsideAlerter } from 'utils/hooks/useOutsideAlerter';
import ProfileDropdown from './ProfileDropdown/ProfileDropdown';
import { useIsHeaderSubHeaderExpanded } from './hooks';
import { useNavigateWithQuery } from 'utils/hooks/useNavigateWithQuery';
import ToggleDarkMode from 'components/ToggleDarkMode/ToggleDarkMode';
import Icon from 'components/Icon/Icon';
import { COLORS } from 'assets/styled';
import { AppMode } from 'store/Common/reducers/common';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import SideMenu from './SideMenu/SideMenu';
import { getLogoNavigationDestinationBasedOnAppMode } from './helpers';
import {
  Cube,
  List,
  GearSix as SettingsIcon,
  User,
} from '@phosphor-icons/react';
import EnvironmentBanner from 'components/EnvironmentBanner/EnvironmentBanner';
import CatalogManagementDropdown from './CatalogManagementDropdown/CatalogManagementDropdown';
import SettingsDropdown from './SettingsDropdown/SettingsDropdown';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isSubHeaderExpanded = useIsHeaderSubHeaderExpanded();
  const { isSmallTablet } = useBreakpointFlag();

  const [sideMenuIsOpen, setSideMenuIsOpen] = useState<boolean>(false);

  const userId = useSelector((state: IRootReducerState) => state.userInfo.id);
  const { appMode, darkMode, isExternalHost } = useSelector(
    (state: IRootReducerState) => state.commonInfo
  );

  const navigateWithQuery = useNavigateWithQuery();

  const [showProfileDropdown, setShowProfileDropdown] =
    useState<boolean>(false);
  const [showCatalogManagementDropdown, setShowCatalogManagementDropdown] =
    useState<boolean>(false);
  const [showSettingsDropdown, setShowSettingsDropdown] =
    useState<boolean>(false);
  const profileDropdownWrapperRef = useRef(null);
  const catalogManagementDropdownWrapperRef = useRef(null);
  const settingsDropdownWrapperRef = useRef(null);
  const sideMenuRef = useRef(null);
  useOutsideAlerter(profileDropdownWrapperRef, () =>
    setShowProfileDropdown(false)
  );
  useOutsideAlerter(catalogManagementDropdownWrapperRef, () =>
    setShowCatalogManagementDropdown(false)
  );
  useOutsideAlerter(settingsDropdownWrapperRef, () =>
    setShowSettingsDropdown(false)
  );
  useOutsideAlerter(sideMenuRef, () => setSideMenuIsOpen(false));

  return (
    <Container>
      <RowSpaceBetween>
        <LeftSide>
          <Icon
            svg={LogyxLogoSVG}
            onClick={() =>
              navigateWithQuery(
                getLogoNavigationDestinationBasedOnAppMode(
                  appMode,
                  location.pathname
                )
              )
            }
            fill={COLORS.BLACK}
            size={123}
            noRipple
          />
          <SelectedModeLabel darkMode={darkMode}>
            {appMode === AppMode.BUYER && t('Buyer Mode')}
            {appMode === AppMode.SUPPLIER && t('Supplier Mode')}
          </SelectedModeLabel>
        </LeftSide>
        <RightSide>
          {!isSmallTablet && (
            <>
              {appMode === AppMode.SUPPLIER ? (
                <NavigationLabel
                  onClick={() => {
                    navigateWithQuery(RoutesConfig.SupplierOrders.fullPath);
                  }}
                  isSelected={
                    location.pathname.includes(
                      RoutesConfig.SupplierOrders.fullPath
                    ) ||
                    location.pathname.includes(
                      RoutesConfig.SupplierNewOrder.fullPath
                    )
                  }
                >
                  {t('Orders')}
                </NavigationLabel>
              ) : null}
              {appMode === AppMode.BUYER ? (
                <>
                  {/* QUOTATIONS DISABLED - the quotation feature is not released
                      to staging yet. This is the only entry point into the flow;
                      the routes are commented out in AppNavigationRouter.tsx and
                      the Templates entry in ModelConfiguration.tsx. Uncomment all
                      three to bring the feature back.
                  <NavigationLabel
                    onClick={() => {
                      navigateWithQuery(RoutesConfig.BuyerQuotations.fullPath);
                    }}
                    isSelected={
                      location.pathname.includes(
                        RoutesConfig.BuyerQuotations.fullPath
                      ) ||
                      location.pathname.includes(
                        RoutesConfig.BuyerNewQuotation.fullPath
                      )
                    }
                  >
                    {t('Quotations')}
                  </NavigationLabel>
                  */}
                  <NavigationLabel
                    onClick={() => {
                      navigateWithQuery(RoutesConfig.BuyerOrders.fullPath);
                    }}
                    isSelected={
                      location.pathname.includes(
                        RoutesConfig.BuyerOrders.fullPath
                      ) ||
                      location.pathname.includes(
                        RoutesConfig.BuyerNewOrder.fullPath
                      )
                    }
                  >
                    {t('Orders')}
                  </NavigationLabel>
                </>
              ) : null}
            </>
          )}
          {isSmallTablet && appMode === AppMode.DEALER_PANEL && (
            <SwitchersWrapper>
              <ToggleDarkMode />
            </SwitchersWrapper>
          )}
          {[AppMode.BUYER, AppMode.SUPPLIER].includes(appMode) &&
          !isExternalHost ? (
            <HeaderIconWrapper ref={catalogManagementDropdownWrapperRef}>
              <Icon
                svg={Cube}
                onClick={() =>
                  setShowCatalogManagementDropdown(
                    !showCatalogManagementDropdown
                  )
                }
                size={30}
                color={COLORS.GREEN}
              />
              {showCatalogManagementDropdown && (
                <CatalogManagementDropdown
                  setShowCatalogManagementDropdown={
                    setShowCatalogManagementDropdown
                  }
                />
              )}
            </HeaderIconWrapper>
          ) : null}
          {[AppMode.BUYER, AppMode.SUPPLIER].includes(appMode) ? (
            <HeaderIconWrapper ref={settingsDropdownWrapperRef}>
              <Icon
                svg={SettingsIcon}
                onClick={() => {
                  setShowSettingsDropdown(!showSettingsDropdown);
                }}
                color={COLORS.GREEN}
                size={30}
              />
              {showSettingsDropdown && (
                <SettingsDropdown
                  setShowSettingsDropdown={setShowSettingsDropdown}
                />
              )}
            </HeaderIconWrapper>
          ) : null}
          {!isSmallTablet && appMode === AppMode.DEALER_PANEL && (
            <>
              <NavigationLabel
                onClick={() => {
                  navigateWithQuery(RoutesConfig.Suppliers.fullPath);
                }}
                isSelected={location.pathname.includes(
                  RoutesConfig.Suppliers.fullPath
                )}
              >
                {t('Suppliers')}
              </NavigationLabel>
              <NavigationLabel
                onClick={() => {
                  navigateWithQuery(RoutesConfig.CustomModels.fullPath);
                }}
                isSelected={location.pathname.includes(
                  RoutesConfig.CustomModels.fullPath
                )}
              >
                {t('Model Editor')}
              </NavigationLabel>
            </>
          )}
          {appMode !== AppMode.EXTERNAL && (
            <SwitchersWrapper>
              <LanguageSwitcher />
            </SwitchersWrapper>
          )}
          {userId ? (
            <div ref={profileDropdownWrapperRef} data-testid="header-profile">
              <Icon
                svg={User}
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                size={30}
                color={COLORS.GREEN}
              />
              {showProfileDropdown && (
                <ProfileDropdown
                  setShowProfileDropdown={setShowProfileDropdown}
                />
              )}
            </div>
          ) : null}
          {isSmallTablet && appMode !== AppMode.EXTERNAL && (
            <Icon
              svg={List}
              color={COLORS.GREEN}
              onClick={() => setSideMenuIsOpen((prev) => !prev)}
              size={30}
            />
          )}
        </RightSide>
      </RowSpaceBetween>
      <EnvironmentBanner />
      <SubHeader isExpanded={isSubHeaderExpanded} />
      {isSmallTablet && appMode !== AppMode.EXTERNAL ? (
        <SideMenu
          ref={sideMenuRef}
          active={sideMenuIsOpen}
          setSideMenuIsOpen={setSideMenuIsOpen}
        />
      ) : null}
    </Container>
  );
};

export default Header;
